import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller that pairs a `<select>` of preset duration options with
 * a free-form numeric input for custom values.
 *
 * When the select value matches {@link customValueValue} (default `-1`), the
 * custom input is shown and enabled; otherwise it is hidden and disabled.
 *
 * On connect, if the current persisted value does not match any preset, the
 * controller automatically switches to custom mode and populates the input.
 *
 * @example
 *   <div data-controller="custom-duration"
 *        data-custom-duration-custom-value-value="-1"
 *        data-custom-duration-presets-value="[30,60,90,180,365,0]"
 *        data-custom-duration-current-value="45">
 *     <select data-custom-duration-target="select"
 *             data-action="custom-duration#toggle">
 *       <option value="30">30 days</option>
 *       <option value="-1">Custom</option>
 *     </select>
 *     <div data-custom-duration-target="customInput" class="d-none">
 *       <input type="number" name="duration">
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["select", "customInput"]
  static values = {
    customValue: { type: Number, default: -1 },
    presets: { type: Array, default: [] },
    current: { type: Number, default: null }
  }

  /**
   * Initialises the controller by detecting non-preset values and
   * setting the correct visibility of the custom input.
   */
  connect() {
    this.handleNonPresetValue()
    this.toggle()
  }

  /**
   * Shows or hides the custom input based on the current select value.
   * Called on connect and whenever the select changes (via `data-action`).
   */
  toggle() {
    const isCustom = parseInt(this.selectTarget.value) === this.customValueValue
    const input = this.customInputTarget.querySelector("input")
    this.customInputTarget.classList.toggle("d-none", !isCustom)
    input.disabled = !isCustom
  }

  /**
   * If the persisted {@link currentValue} is not found in {@link presetsValue},
   * switches the select to the custom sentinel and populates the custom input
   * with the current value so the user sees their non-standard duration.
   */
  handleNonPresetValue() {
    const currentValue = this.currentValue
    if (currentValue === null) return

    const isPreset = this.presetsValue.includes(currentValue)

    if (!isPreset && currentValue !== this.customValueValue) {
      const input = this.customInputTarget.querySelector("input")
      input.value = currentValue
      this.selectTarget.value = this.customValueValue
    }
  }
}
